import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import SidebarEscritorio from '../components/sala/SidebarEscritorio';
import RedNeuronal2D from '../components/sala/RedNeuronal2D';
import ModalPensamiento from '../components/sala/ModalPensamiento';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileFeedSala from '../components/mobile/MobileFeedSala';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', morado:'#534AB7', moradoClaro:'#AFA9EC' };

export default function SalaPage() {
  const { user, profile, membership } = useAuth();
  const { isMobile, w } = useScreen();
  const isMobileView = useIsMobile();
  const navigate = useNavigate();
  const [emprendedores, setEmprendedores] = useState([]);
  const [pensamientos, setPensamientos] = useState([]);
  const [sinapsis, setSinapsis] = useState([]);
  const [pensamientoSeleccionado, setPensamientoSeleccionado] = useState(null);
  const [highlightWikilinks, setHighlightWikilinks] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Mobile sala sub-views
  const [salaView, setSalaView] = useState('pensamientos');
  const [proyectos, setProyectos] = useState([]);
  const [proyectosLoaded, setProyectosLoaded] = useState(false);
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [giroFiltro, setGiroFiltro] = useState('Todos');
  const [redLoaded, setRedLoaded] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      const { data: profs } = await supabase.from('profiles').select('id, full_name, business_name, business_type, avatar_url, membership, identidad').eq('onboarding_done', true);
      setEmprendedores((profs || []).map(p => ({ ...p, user_id: p.id })));

      const { data: pens } = await supabase.from('pensamientos').select('*').eq('visibilidad', 'publico').order('created_at', { ascending: false });
      setPensamientos(pens || []);

      const { data: sins } = await supabase.from('sinapsis').select('*');
      // Keep all sinapsis — the renderer handles positioning via chainParent
      const dbSinapsis = (sins || [])
        .map(s => ({
          from_id: s.pensamiento_from_id || s.user_from_id,
          to_id: s.tipo === 'cluster' ? (s.user_from_id || s.user_to_id) : (s.pensamiento_to_id || s.user_to_id),
          tipo: s.tipo,
        }));
      // BUG 3 fix: generate virtual principal sinapsis from T&D hub to each emprendedor
      const sinapsisPrincipales = (profs || []).map(emp => ({
        from_id: 'td',
        to_id: emp.id,
        tipo: 'principal',
      }));
      setSinapsis([...sinapsisPrincipales, ...dbSinapsis]);
    };
    cargar();
  }, []);

  const misPensCount = pensamientos.filter(p => p.user_id === user?.id).length;

  // Load proyectos when switching to that tab
  useEffect(() => {
    if (salaView === 'proyectos' && !proyectosLoaded) {
      (async () => {
        try {
          let { data } = await supabase
            .from('arbol_cartas_publicas')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50);
          if (!data) throw new Error('view missing');
          setProyectos(data);
        } catch {
          const { data } = await supabase
            .from('arbol_negocios')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50);
          setProyectos(data || []);
        }
        setProyectosLoaded(true);
      })();
    }
  }, [salaView, proyectosLoaded]);

  if (isMobileView) {
    const giros = ['Todos', ...Array.from(new Set(proyectos.map(p => p.giro).filter(Boolean)))];
    const proyectosFiltrados = proyectos.filter(p => {
      const matchBusqueda = !busquedaProyecto || (p.nombre || '').toLowerCase().includes(busquedaProyecto.toLowerCase()) || (p.giro || '').toLowerCase().includes(busquedaProyecto.toLowerCase());
      const matchGiro = giroFiltro === 'Todos' || p.giro === giroFiltro;
      return matchBusqueda && matchGiro;
    });

    return (
      <div style={{ background: '#0a0a0f', minHeight: '100vh', fontFamily: font, color: '#fff', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 40,
          background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '0.5px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{
              fontFamily: serif, fontSize: 22, margin: 0, fontWeight: 400,
              background: 'linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>T&D</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {membership === 'free_observer' && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>FREE</span>
              )}
              <div onClick={() => navigate('/perfil')} style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                background: 'linear-gradient(135deg, #534AB7, #1D9E75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600, color: '#fff',
              }}>
                {(profile?.full_name || 'U')[0]}
              </div>
            </div>
          </div>

          {/* Tab switcher pills */}
          <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {[{id:'pensamientos',label:'Pensamientos'},{id:'proyectos',label:'Proyectos'},{id:'red',label:'Red neuronal'}].map(t => (
              <button key={t.id} onClick={() => { setSalaView(t.id); if (t.id === 'red') setRedLoaded(true); }} style={{
                padding: '6px 16px', borderRadius: 20, border: `1px solid ${salaView===t.id ? '#5dc1b9' : '#1E2740'}`,
                background: salaView===t.id ? 'rgba(93,193,185,.12)' : 'transparent',
                color: salaView===t.id ? '#5dc1b9' : '#8C97B0',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: font,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Proyectos view */}
        {salaView === 'proyectos' && (
          <div style={{ flex: 1, paddingBottom: 80 }}>
            {/* Search */}
            <div style={{ padding: '12px 16px' }}>
              <input
                type="text" value={busquedaProyecto} onChange={e => setBusquedaProyecto(e.target.value)}
                placeholder="Buscar proyecto..."
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 12,
                  background: '#111726', border: '1px solid #1E2740', color: '#F3F4F6',
                  fontSize: 14, fontFamily: font, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Giro chips */}
            <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {giros.map(g => (
                <button key={g} onClick={() => setGiroFiltro(g)} style={{
                  padding: '4px 14px', borderRadius: 16, fontSize: 12, fontWeight: 500,
                  border: `1px solid ${giroFiltro === g ? '#5dc1b9' : '#1E2740'}`,
                  background: giroFiltro === g ? 'rgba(93,193,185,.12)' : 'transparent',
                  color: giroFiltro === g ? '#5dc1b9' : '#8C97B0',
                  cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap', flexShrink: 0,
                }}>{g}</button>
              ))}
            </div>

            {/* Project cards */}
            {!proyectosLoaded ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#8C97B0' }}>Cargando proyectos...</div>
            ) : proyectosFiltrados.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#6B7084' }}>No se encontraron proyectos</div>
            ) : (
              proyectosFiltrados.map(project => (
                <div key={project.id} onClick={() => navigate('/proyecto/' + (project.user_id || project.id))} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                  borderBottom: '1px solid #1E2740', cursor: 'pointer',
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%', background: '#111726',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700, color: '#FDBE02', flexShrink: 0,
                  }}>
                    {(project.nombre || 'N')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#F3F4F6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.nombre}</div>
                    <div style={{ fontSize: 12, color: '#8C97B0' }}>{project.giro || 'Sin giro'}{project.city ? ` · ${project.city}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#6B7084', letterSpacing: 1 }}>SALUD ADMIN.</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: (project.salud_admin || 0) >= 70 ? '#5dc1b9' : (project.salud_admin || 0) >= 40 ? '#FDBE02' : '#eb636b' }}>
                      {project.salud_admin || 0}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pensamientos view */}
        {salaView === 'pensamientos' && (
          <MobileFeedSala
            pensamientos={pensamientos}
            emprendedores={emprendedores}
            sinapsis={sinapsis}
            user={user}
            profile={profile}
            membership={membership}
            onNewPensamiento={(nuevo) => {
              setPensamientos(prev => [nuevo, ...prev]);
            }}
          />
        )}

        {/* Red neuronal view */}
        {salaView === 'red' && (
          <div style={{ flex: 1, paddingBottom: 80 }}>
            {redLoaded && (
              <div style={{ touchAction: 'manipulation', overflow: 'auto', height: 'calc(100vh - 200px)' }}>
                <RedNeuronal2D
                  emprendedores={emprendedores}
                  pensamientos={pensamientos}
                  sinapsis={sinapsis}
                  userId={user?.id}
                  highlightWikilinks={highlightWikilinks}
                  onClickPensamiento={(id) => setPensamientoSeleccionado(id)}
                  onClickEmprendedor={(uid) => navigate('/proyecto/' + uid)}
                />
              </div>
            )}
          </div>
        )}

        {/* Modal pensamiento */}
        {pensamientoSeleccionado && (
          <ModalPensamiento
            pensamientoId={pensamientoSeleccionado}
            currentUserId={user?.id}
            membership={membership}
            onCerrar={() => setPensamientoSeleccionado(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff' }}>
      {/* Header */}
      <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {isMobile && <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background:'none', border:'none', color:'#fff', fontSize:18, cursor:'pointer' }}>☰</button>}
          <span style={{ fontFamily:serif, fontSize:17, background:'linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>T&D</span>
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:999, background:'rgba(83,74,183,0.18)', color:C.moradoClaro }}>La Sala</span>
          {membership === 'free_observer' && <span style={{ fontSize:11, padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.55)' }}>FREE OBSERVER</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
<div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600 }}>
            {(profile?.full_name || 'U')[0]}
          </div>
        </div>
      </header>

      <div style={{ display:'flex', minHeight:'calc(100vh - 50px)' }}>
        {/* Sidebar */}
        {w >= 900 && (sidebarOpen || !isMobile) && (
          <SidebarEscritorio membership={membership} currentView="sala" pensamientosCount={misPensCount} />
        )}

        {/* Main */}
        <main style={{ flex:1, padding:isMobile?'16px 12px':'20px 24px', overflowY:'auto' }}>
          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:10, color:'rgba(93,193,185,0.8)', letterSpacing:1.5, textTransform:'uppercase', margin:'0 0 4px' }}>La red está viva</p>
            <h1 style={{ fontFamily:serif, fontSize:22, fontWeight:400, margin:0 }}>Bienvenido, {(profile?.full_name || 'emprendedor').split(' ')[0]}</h1>
          </div>

          {/* Red 2D */}
          <div style={{ background:'radial-gradient(ellipse at center, rgba(83,74,183,0.12), transparent 70%)', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.05)', padding:10, userSelect:'none', marginBottom:16 }}>
            <RedNeuronal2D
              emprendedores={emprendedores}
              pensamientos={pensamientos}
              sinapsis={sinapsis}
              userId={user?.id}
              highlightWikilinks={highlightWikilinks}
              onClickPensamiento={(id) => setPensamientoSeleccionado(id)}
              onClickEmprendedor={(uid) => navigate('/proyecto/' + uid)}
            />
          </div>

          {/* Leyenda */}
          <div style={{ padding:'10px 12px', background:'rgba(255,255,255,0.03)', borderRadius:8, border:'0.5px solid rgba(255,255,255,0.06)' }}>
            <p style={{ margin:'0 0 7px', fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:1.2, textTransform:'uppercase' }}>4 tipos de sinapsis</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:14, rowGap:6, fontSize:11, color:'rgba(255,255,255,0.7)' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:22, height:2, background:C.turquesa, display:'inline-block', borderRadius:1 }} /> T&D ↔ emprendedor</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:22, height:2, background:C.mango, display:'inline-block', borderRadius:1, opacity:0.6 }} /> cluster interno</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:22, height:2, background:C.turquesa, display:'inline-block', borderRadius:1, borderStyle:'dashed' }} /> wikilink [[ ]]</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:22, height:2, background:C.mango, display:'inline-block', borderRadius:1, opacity:0.5 }} /> afinidad (Fase 2)</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5, marginLeft:8, borderLeft:'0.5px solid rgba(255,255,255,0.1)', paddingLeft:10 }}><span style={{ width:8, height:8, borderRadius:'50%', background:'#FDBE02', display:'inline-block' }} /> Thinker</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:8, height:8, borderRadius:'50%', background:'#5dc1b9', display:'inline-block' }} /> Doer</span>
            </div>
          </div>
        </main>
      </div>

      {/* Modal pensamiento */}
      {pensamientoSeleccionado && (
        <ModalPensamiento
          pensamientoId={pensamientoSeleccionado}
          currentUserId={user?.id}
          membership={membership}
          onCerrar={() => setPensamientoSeleccionado(null)}
        />
      )}

    </div>
  );
}
